// TelcoTrack — data pro graf ziskovosti stavby (hotovost vs vyčerpání PPD příjmů).

import type { FinanceComparisonResult } from '../../core/models/object.model';
import {
  CONSTRUCTION_CATEGORY_FINANCE_WORK_BUCKETS,
  CONSTRUCTION_PROGRESS_CATEGORY_IDS,
  CONSTRUCTION_PROGRESS_CATEGORY_LABELS,
  type ConstructionProgressCategoryId,
  buildConstructionCategoryProgressRows,
  weightedOverallConstructionCompletion,
  type ConstructionCategoryProgressRow,
} from '@core/shared-bridge';

export interface FinanceProfitabilityChartRow {
  id: ConstructionProgressCategoryId | 'celkem';
  label: string;
  /** Hotovost stavby (0–100 %). */
  completionPercent: number;
  /** Podíl vyčerpaných nákladů vůči plánovanému příjmu práce (0–∞ %). */
  incomeSpentPercent: number | null;
  /** Plánovaný příjem práce (PPD) v Kč. */
  plannedIncome: number;
  /** Skutečné náklady práce z výkazu v Kč (bez materiálu). */
  actualCosts: number;
  /** plannedIncome − actualCosts */
  margin: number;
  /** true když vyčerpání > hotovost (riziko prodělku). */
  isLossRisk: boolean;
  objectCount: number;
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function roundPercent(n: number): number {
  return Math.round(n * 10) / 10;
}

function sumAllWorkBuckets(
  comparison: FinanceComparisonResult,
): { planned: number; actual: number } {
  let planned = 0;
  let actual = 0;
  for (const bucket of comparison.buckets ?? []) {
    if (bucket.kind !== 'work') continue;
    planned += Number(bucket.planned ?? 0);
    actual += Number(bucket.actual ?? 0);
  }
  return { planned: roundMoney(planned), actual: roundMoney(actual) };
}

function sumWorkBucketAmounts(
  comparison: FinanceComparisonResult,
  bucketSuffixes: readonly string[],
): { planned: number; actual: number } {
  const buckets = comparison.buckets ?? [];
  let planned = 0;
  let actual = 0;
  for (const bucket of buckets) {
    if (bucket.kind !== 'work') continue;
    const suffix = bucket.id.replace(/^work-/, '');
    if (!bucketSuffixes.includes(suffix)) continue;
    planned += Number(bucket.planned ?? 0);
    actual += Number(bucket.actual ?? 0);
  }
  return { planned: roundMoney(planned), actual: roundMoney(actual) };
}

function incomeSpentPercent(plannedIncome: number, actualCosts: number): number | null {
  if (plannedIncome <= 0) return null;
  return roundPercent((actualCosts / plannedIncome) * 100);
}

export function buildFinanceProfitabilityChartRows(args: {
  comparison: FinanceComparisonResult;
  progressByTypeAndPhase?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  /** Preferováno — zahrnuje milníky BUDOVA/ROZVADEC. */
  completionByCategory?: ReadonlyArray<{
    id: string;
    label: string;
    completionPercent: number;
    objectCount: number;
  }>;
}): FinanceProfitabilityChartRow[] {
  const { comparison } = args;
  const progressRows: ConstructionCategoryProgressRow[] = args.completionByCategory?.length
    ? CONSTRUCTION_PROGRESS_CATEGORY_IDS.map((id) => {
        const fromApi = args.completionByCategory!.find((r) => r.id === id);
        return {
          id,
          label: fromApi?.label ?? CONSTRUCTION_PROGRESS_CATEGORY_LABELS[id],
          completionPercent: fromApi?.completionPercent ?? 0,
          objectCount: fromApi?.objectCount ?? 0,
        };
      })
    : args.progressByTypeAndPhase
      ? buildConstructionCategoryProgressRows({ byObjectTypeAndPhaseCode: args.progressByTypeAndPhase })
      : CONSTRUCTION_PROGRESS_CATEGORY_IDS.map((id) => ({
          id,
          label: CONSTRUCTION_PROGRESS_CATEGORY_LABELS[id],
          completionPercent: 0,
          objectCount: 0,
        }));

  const progressById = new Map(progressRows.map((r) => [r.id, r]));

  const categoryRows: FinanceProfitabilityChartRow[] = CONSTRUCTION_PROGRESS_CATEGORY_IDS.map((id) => {
    const progress = progressById.get(id);
    const workBuckets = CONSTRUCTION_CATEGORY_FINANCE_WORK_BUCKETS[id];
    const { planned: plannedIncome, actual: actualWork } = sumWorkBucketAmounts(comparison, [...workBuckets]);
    const actualCosts = roundMoney(actualWork);
    const spent = incomeSpentPercent(plannedIncome, actualCosts);
    const completion = progress?.completionPercent ?? 0;

    return {
      id,
      label: CONSTRUCTION_PROGRESS_CATEGORY_LABELS[id],
      completionPercent: completion,
      incomeSpentPercent: spent,
      plannedIncome,
      actualCosts,
      margin: roundMoney(plannedIncome - actualCosts),
      isLossRisk: spent != null && spent > completion,
      objectCount: progress?.objectCount ?? 0,
    };
  });

  const workFromBuckets = sumAllWorkBuckets(comparison);
  const headerPlanned = Number(comparison.plannedWorkRevenue ?? 0);
  const totalPlannedIncome =
    workFromBuckets.planned > 0 ? workFromBuckets.planned : roundMoney(headerPlanned);
  const totalActualCosts = Number(comparison.actualWorkCost ?? 0);
  const totalSpent = incomeSpentPercent(totalPlannedIncome, totalActualCosts);
  const overallCompletion = weightedOverallConstructionCompletion(progressRows);

  categoryRows.push({
    id: 'celkem',
    label: 'Celkem',
    completionPercent: overallCompletion,
    incomeSpentPercent: totalSpent,
    plannedIncome: totalPlannedIncome,
    actualCosts: roundMoney(totalActualCosts),
    margin: roundMoney(totalPlannedIncome - totalActualCosts),
    isLossRisk: totalSpent != null && totalSpent > overallCompletion,
    objectCount: progressRows.reduce((acc, r) => acc + r.objectCount, 0),
  });

  return categoryRows;
}

export function profitabilityStatusLabel(rows: FinanceProfitabilityChartRow[]): {
  label: string;
  tone: 'green' | 'amber' | 'red';
  detail: string;
} {
  const total = rows.find((r) => r.id === 'celkem');
  if (!total) {
    return { label: '—', tone: 'amber', detail: 'Chybí data pro vyhodnocení.' };
  }
  if (total.incomeSpentPercent == null) {
    return { label: 'Bez PPD příjmu', tone: 'amber', detail: 'Pro srovnání s příjmem chybí PPD práce.' };
  }
  const gap = total.incomeSpentPercent - total.completionPercent;
  if (gap <= 0 && total.margin >= 0) {
    return {
      label: 'Výdělečná',
      tone: 'green',
      detail: `Hotovost ${total.completionPercent} %, vyčerpání příjmů ${total.incomeSpentPercent} %.`,
    };
  }
  if (gap <= 0) {
    return {
      label: 'Na hraně',
      tone: 'amber',
      detail: `Vyčerpáno ${total.incomeSpentPercent} % příjmů při hotovosti ${total.completionPercent} %, ale rozdíl je záporný.`,
    };
  }
  return {
    label: 'Riziko prodělku',
    tone: 'red',
    detail: `Vyčerpáno ${total.incomeSpentPercent} % příjmů při hotovosti jen ${total.completionPercent} %.`,
  };
}
